import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { useGroupFilter } from '../../../groupFilter'
import { useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

const query = gql`
  query trendLocationIntradaySales(
    $iDate: Date!
    $iFilter: JSON!
    $useLocationGroup: Boolean!
  ) {
    trendLocationIntradaySales(iDate: $iDate, iFilter: $iFilter)
      @skip(if: $useLocationGroup) {
      nodes {
        hour
        netSales
        cumulativeSales
        netSalesBudget
        cumulativeLabor
        cumulativeLaborPercent
        burdenedLabor
        burdenedLaborPercent
        lastYearSalesPlusTenPercent
      }
    }

    trendLocationGroupIntradaySales(iDate: $iDate, iFilter: $iFilter)
      @include(if: $useLocationGroup) {
      nodes {
        hour
        netSales
        cumulativeSales
        netSalesBudget
        cumulativeLabor
        cumulativeLaborPercent
        burdenedLabor
        burdenedLaborPercent
        lastYearSalesPlusTenPercent
      }
    }
  }
`

export const intradaySalesConfigs = {
  hour: 'string',
  formattedHour: 'string',
  netSales: 'price',
  cumulativeSales: 'price',
  netSalesBudget: 'price',
  cumulativeLabor: 'price',
  cumulativeLaborPercent: 'percent',
  burdenedLabor: 'price',
  burdenedLaborPercent: 'percent',
  lastYearSalesPlusTenPercent: 'price',
} as const

const useIntradaySales = () => {
  const { groupFilter } = useGroupFilter()
  const { variables } = useVariables()

  const queryVariables = useMemo(() => {
    const today = moment().format('YYYY-MM-DD')

    if (groupFilter)
      return {
        iDate: today,
        iFilter: { location_ids: groupFilter.ids },
        useLocationGroup: false,
      }

    if (variables.allStores)
      return {
        iDate: today,
        iFilter: { location_group_ids: variables.allStores.locationGroupIds },
        useLocationGroup: true,
      }
  }, [groupFilter, variables])

  const { data, loading } = useQuery(query, {
    variables: queryVariables,
    skip: !queryVariables,
  })

  return {
    data: useMemo((): IApiDataType => {
      const trendLocationIntradaySales =
        data?.trendLocationIntradaySales?.nodes ||
        data?.trendLocationGroupIntradaySales?.nodes

      if (!trendLocationIntradaySales) return null

      return trendLocationIntradaySales.map(
        (n: { hour: string; netSales: number }) => {
          const formattedHour = moment(n.hour).format('h:mmA')

          return {
            ...n,
            formattedHour: formattedHour,
          }
        },
      )
    }, [data]),
    loading,
  }
}

export default useIntradaySales
